/**
 * Isolated review mode (Apple App Review account).
 * This module may only touch review_* tables and review RPCs; the database
 * denies every production table to review accounts (restrictive RLS).
 */
import { supabase } from "@/integrations/supabase/client";

export const REVIEW_TABLES = ["review_clients", "review_appointments", "review_quotes"] as const;

export type ReviewClient = {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  birth_date: string | null;
  notes: string | null;
  anamnesis: Record<string, unknown>;
};
export type ReviewAppointment = {
  id: string;
  client_id: string;
  professional_name: string;
  procedure_name: string;
  starts_at: string;
  duration_min: number;
  status: "scheduled" | "confirmed" | "completed" | "cancelled";
  price_cents: number;
  notes: string | null;
};
export type ReviewQuote = {
  id: string;
  client_id: string;
  title: string;
  items: { name: string; qty: number; unit_cents: number }[];
  total_cents: number;
  status: "draft" | "sent" | "approved" | "declined";
};

export async function isReviewAccount(): Promise<boolean> {
  const { data, error } = await supabase.rpc("am_i_review_account");
  if (error) throw error;
  return data === true;
}

async function ownerId(): Promise<string> {
  const { data } = await supabase.auth.getUser();
  if (!data.user) throw new Error("Sessão expirada");
  return data.user.id;
}

export async function listClients(): Promise<ReviewClient[]> {
  const { data, error } = await supabase.from("review_clients").select("*").order("full_name");
  if (error) throw error;
  return (data ?? []) as unknown as ReviewClient[];
}

export async function createClient(input: { full_name: string; phone?: string; email?: string }) {
  const name = input.full_name.trim();
  if (name.length < 2) throw new Error("Informe o nome");
  const { error } = await supabase.from("review_clients").insert({
    owner_id: await ownerId(),
    full_name: name,
    phone: input.phone?.trim() || null,
    email: input.email?.trim() || null,
    notes: "Cadastro fictício criado no modo de revisão.",
  });
  if (error) throw error;
}

export async function saveAnamnesis(clientId: string, anamnesis: Record<string, unknown>) {
  const { error } = await supabase.from("review_clients").update({ anamnesis: anamnesis as never }).eq("id", clientId);
  if (error) throw error;
}

export async function listAppointments(): Promise<ReviewAppointment[]> {
  const { data, error } = await supabase.from("review_appointments").select("*").order("starts_at");
  if (error) throw error;
  return (data ?? []) as ReviewAppointment[];
}

export async function createAppointment(input: {
  client_id: string; professional_name: string; procedure_name: string;
  starts_at: string; duration_min: number; price_cents: number;
}) {
  if (!input.client_id) throw new Error("Selecione o cliente");
  if (!input.professional_name) throw new Error("Selecione o profissional");
  if (!(input.duration_min >= 5 && input.duration_min <= 480)) throw new Error("Duração entre 5 e 480 minutos");
  const { error } = await supabase.from("review_appointments").insert({ ...input, owner_id: await ownerId() });
  if (error) throw error;
}

export async function setAppointmentStatus(id: string, status: ReviewAppointment["status"]) {
  const { error } = await supabase.from("review_appointments").update({ status }).eq("id", id);
  if (error) throw error;
}

export async function listQuotes(): Promise<ReviewQuote[]> {
  const { data, error } = await supabase.from("review_quotes").select("*").order("created_at");
  if (error) throw error;
  return (data ?? []) as unknown as ReviewQuote[];
}

export async function setQuoteStatus(id: string, status: ReviewQuote["status"]) {
  const { error } = await supabase.from("review_quotes").update({ status }).eq("id", id);
  if (error) throw error;
}

export async function resetSampleData() {
  const { error } = await supabase.rpc("review_reset_my_data");
  if (error) throw error;
}

export const formatCents = (c: number) =>
  (c / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
